import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { StatusBadge } from '../components/common/StatusBadge';
import { 
  Plus, 
  Search, 
  FolderOpen,
  Users,
  Calendar,
  Clock,
  Pencil,
  Trash2,
  Building,
  ArrowRight,
  CornerDownRight,
  MapPin,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Filter,
  X,
  GraduationCap
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { getAuthToken } from '../lib/authToken';


const SortHeadBase = ({ k, label, className = '', sort, onToggle }) => (
  <button type="button" onClick={() => onToggle(k)} className={`flex items-center gap-1 uppercase tracking-wide text-left hover:text-slate-800 ${sort.key === k ? 'text-indigo-600' : ''} ${className}`} data-testid={`sort-${k}`}>
    {label}{sort.key === k ? (sort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
  </button>
);
const FilterInputBase = ({ k, placeholder, type = 'text', value, onChange }) => (
  <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-7 text-xs px-2" data-testid={`filter-col-${k}`} />
);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DossiersPage = () => {
  const [dossiers, setDossiers] = useState([]);
  const [formations, setFormations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [showFilters, setShowFilters] = useState(false);
  const [colFilters, setColFilters] = useState({ code: '', intitule: '', formation: '', client: '', financeur: '', entreprise: '', lieu: '', date_min: '', date_max: '', candidats_min: '' });
  const [quick, setQuick] = useState({ client: 'all', financeur: 'all', entreprise: 'all', lieu: 'all', duree: 'all' });
  const [dossiersFinanceurs, setDossiersFinanceurs] = useState([]);
  const [aftsManquantes, setAftsManquantes] = useState({});
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statutFilter = searchParams.get('statut') || 'en_cours';
  const typeFilter = searchParams.get('type') || 'tous'; // tous | af | aft
  const setTypeFilter = (t) => { if (t === 'tous') searchParams.delete('type'); else searchParams.set('type', t); setSearchParams(searchParams, { replace: true }); };
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const setStatutFilter = (statut) => {
    searchParams.set('statut', statut); // 'en_cours' par défaut quand le paramètre est absent
    setSearchParams(searchParams, { replace: true });
  };

  const fetchData = useCallback(async () => {
    try {
      const [dossiersRes, formationsRes, dfRes, aftsRes] = await Promise.all([
        axios.get(`${API_URL}/dossiers`, { headers }),
        axios.get(`${API_URL}/formations`, { headers }),
        axios.get(`${API_URL}/dossiers-financeurs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/dossiers-afts-manquantes`, { headers }).catch(() => ({ data: [] })),
      ]);
      setDossiers(dossiersRes.data);
      setFormations(formationsRes.data);
      setDossiersFinanceurs(dfRes.data || []);
      setAftsManquantes(Object.fromEntries((aftsRes.data || []).map(d => [d.id, d.modules])));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/dossiers/${deleteId}`, { headers });
      setDossiers(dossiers.filter(d => d.id !== deleteId));
      toast.success('Dossier supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteId(null);
    }
  };

  const getFormationTitle = (formationId) => {
    const formation = formations.find(f => f.id === formationId);
    return formation?.titre || 'Formation inconnue';
  };

  const getFormationDuree = (formationId) => {
    const formation = formations.find(f => f.id === formationId);
    return formation?.duree_totale_heures || 0;
  };

  // Dossiers financeurs par AF → financeurs et entreprises finales distincts
  const dfParAf = useMemo(() => {
    const map = {};
    dossiersFinanceurs.forEach(df => {
      if (!map[df.action_formation_id]) map[df.action_formation_id] = { financeurs: [], entreprises: [], codes: [] };
      const e = map[df.action_formation_id];
      if (df.financeur_nom && !e.financeurs.includes(df.financeur_nom)) e.financeurs.push(df.financeur_nom);
      const ent = (df.entreprise_finale || '').trim();
      if (ent && !e.entreprises.includes(ent)) e.entreprises.push(ent);
      if (df.code) e.codes.push(df.code);
    });
    return map;
  }, [dossiersFinanceurs]);
  // AFT : financeur propre, sinon dossier financeur de l'AF mère
  const getFinanceurs = (d) => (dfParAf[d.id]?.financeurs || []).join(', ') || (d.type_dossier === 'aft' ? (d.financeur || (dfParAf[d.af_mere_id]?.financeurs || []).join(', ')) : '');
  const getEntreprises = (d) => (dfParAf[d.id]?.entreprises || []).join(', ') || (d.type_dossier === 'aft' ? (dfParAf[d.af_mere_id]?.entreprises || []).join(', ') : '');

  const today = format(new Date(), 'yyyy-MM-dd');
  const dossierById = Object.fromEntries(dossiers.map(d => [d.id, d]));
  // Une AFT suit le statut de son AF mère (visible tant que la mère est visible)
  const mere = (d) => (d.type_dossier === 'aft' && d.af_mere_id && dossierById[d.af_mere_id]) ? dossierById[d.af_mere_id] : d;
  // Terminée = date de fin dépassée (automatique) ; Clôturée / Abandon = décision administrative (prioritaire)
  const estCloturee = (d) => mere(d).statut === 'cloture';
  const estAbandon = (d) => mere(d).statut === 'abandon';
  const estTerminee = (d) => { const m = mere(d); return !estCloturee(d) && !estAbandon(d) && (m.statut === 'termine' || (!!m.date_fin && m.date_fin < today)); };
  const estEnCours = (d) => mere(d).statut === 'en_cours' && !estTerminee(d);
  const statutDe = (d) => estCloturee(d) ? 'cloture' : estAbandon(d) ? 'abandon' : estTerminee(d) ? 'termine' : 'en_cours';
  const statutCounts = {
    tous: dossiers.length,
    en_cours: dossiers.filter(estEnCours).length,
    termine: dossiers.filter(estTerminee).length,
    cloture: dossiers.filter(estCloturee).length,
    abandon: dossiers.filter(estAbandon).length,
  };

  const inc = (v, f) => !f || (v || '').toLowerCase().includes(f.toLowerCase());
  const nbFiltresActifs = Object.values(colFilters).filter(Boolean).length;
  const nbQuickActifs = Object.values(quick).filter(v => v !== 'all').length;
  const typeCounts = { tous: dossiers.length, af: dossiers.filter(d => d.type_dossier !== 'aft').length, aft: dossiers.filter(d => d.type_dossier === 'aft').length };
  const filteredDossiers = dossiers.filter(dossier =>
    (typeFilter === 'tous' || (typeFilter === 'aft') === (dossier.type_dossier === 'aft')) &&
    (statutFilter === 'tous' || statutDe(dossier) === statutFilter) &&
    (dossier.intitule.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dossier.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (dossier.client || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    getFinanceurs(dossier).toLowerCase().includes(searchTerm.toLowerCase()) ||
    getEntreprises(dossier).toLowerCase().includes(searchTerm.toLowerCase())) &&
    (quick.client === 'all' || (dossier.client || '') === quick.client) &&
    (quick.financeur === 'all' || (dfParAf[dossier.id]?.financeurs || []).includes(quick.financeur)) &&
    (quick.entreprise === 'all' || (dfParAf[dossier.id]?.entreprises || []).includes(quick.entreprise)) &&
    (quick.lieu === 'all' || (dossier.lieu_principal || '') === quick.lieu) &&
    (quick.duree === 'all' || getFormationDuree(dossier.formation_id) === Number(quick.duree)) &&
    inc(dossier.code, colFilters.code) && inc(dossier.intitule, colFilters.intitule) && inc(getFormationTitle(dossier.formation_id), colFilters.formation) &&
    inc(dossier.client, colFilters.client) && inc(getFinanceurs(dossier), colFilters.financeur) && inc(getEntreprises(dossier), colFilters.entreprise) && inc(dossier.lieu_principal, colFilters.lieu) &&
    (!colFilters.date_min || (dossier.date_fin || dossier.date_debut || '') >= colFilters.date_min) &&
    (!colFilters.date_max || (dossier.date_debut || '') <= colFilters.date_max) &&
    (!colFilters.candidats_min || (dossier.candidats?.length || 0) >= Number(colFilters.candidats_min))
  );

  const SORT_VALUE = {
    code: d => d.code || '', intitule: d => d.intitule || '', formation: d => getFormationTitle(d.formation_id) || '', statut: d => d.statut || '',
    client: d => d.client || '', financeur: d => getFinanceurs(d), entreprise: d => getEntreprises(d), lieu: d => d.lieu_principal || '', dates: d => d.date_debut || '', candidats: d => d.candidats?.length || 0,
  };
  if (sort.key) {
    const get = SORT_VALUE[sort.key];
    filteredDossiers.sort((a, b) => {
      const va = get(a), vb = get(b);
      const r = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), 'fr', { sensitivity: 'base', numeric: true });
      return sort.dir === 'asc' ? r : -r;
    });
  }
  const toggleSort = (key) => setSort(prev => prev.key === key ? (prev.dir === 'asc' ? { key, dir: 'desc' } : { key: null, dir: 'asc' }) : { key, dir: 'asc' });
  const setCF = (k) => (v) => setColFilters(prev => ({ ...prev, [k]: v }));
  const setQ = (k) => (v) => setQuick(prev => ({ ...prev, [k]: v }));
  const distinct = (fn) => [...new Set(dossiers.map(fn).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'fr', { numeric: true }));
  const clientsDispo = distinct(d => d.client);
  const lieuxDispo = distinct(d => d.lieu_principal);
  const financeursDispo = [...new Set(Object.values(dfParAf).flatMap(e => e.financeurs))].sort((a, b) => a.localeCompare(b, 'fr'));
  const entreprisesDispo = [...new Set(Object.values(dfParAf).flatMap(e => e.entreprises))].sort((a, b) => a.localeCompare(b, 'fr'));
  const dureesDispo = [...new Set(dossiers.map(d => getFormationDuree(d.formation_id)).filter(h => h > 0))].sort((a, b) => a - b);

  // Regroupement des AF liées (mère suivie de ses AF rattachées) + pastille de couleur par groupe
  const GROUP_COLORS = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-sky-500', 'bg-violet-500', 'bg-orange-500', 'bg-teal-500'];
  const displayRows = (() => {
    const byId = Object.fromEntries(filteredDossiers.map(d => [d.id, d]));
    const parentOf = {};
    dossiers.forEach(d => (d.af_liees || []).forEach(cid => { parentOf[cid] = d.id; }));
    const parents = dossiers.filter(d => (d.af_liees || []).length > 0);
    const colorByParent = {};
    parents.forEach((p, i) => { colorByParent[p.id] = GROUP_COLORS[i % GROUP_COLORS.length]; });
    const rows = [];
    const added = new Set();
    const pushWithChildren = (d, color, isChild) => {
      rows.push({ dossier: d, isChild, color });
      added.add(d.id);
      (d.af_liees || []).forEach(cid => {
        const c = byId[cid];
        if (c && !added.has(cid)) pushWithChildren(c, color, true);
      });
    };
    filteredDossiers.forEach(d => {
      if (added.has(d.id)) return;
      const parentId = parentOf[d.id];
      if (parentId && byId[parentId] && !added.has(parentId)) return; // insérée après sa mère
      const rootColor = (d.af_liees || []).length > 0 ? colorByParent[d.id] : (parentId ? colorByParent[parentId] : null);
      pushWithChildren(d, rootColor, false);
    });
    return rows;
  })();

  const statutTabs = [
    { key: 'tous', label: 'Tous', activeClass: 'bg-slate-900 text-white' },
    { key: 'en_cours', label: 'En cours', activeClass: 'bg-blue-600 text-white' },
    { key: 'termine', label: 'Terminés', activeClass: 'bg-emerald-600 text-white' },
    { key: 'cloture', label: 'Clôturés', activeClass: 'bg-slate-600 text-white' },
    { key: 'abandon', label: 'Abandon', activeClass: 'bg-amber-600 text-white' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dossiers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Actions de formation</h1>
            <p className="text-slate-500 mt-1">{dossiers.length} action(s)</p>
          </div>
          <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
            <Link to="/dossiers/new" data-testid="new-dossier-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle action
            </Link>
          </Button>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher une action..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-dossiers"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="statut-filters">
            {statutTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatutFilter(tab.key)}
                data-testid={`filter-statut-${tab.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  statutFilter === tab.key
                    ? tab.activeClass
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {tab.label} ({statutCounts[tab.key]})
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-full bg-slate-100 p-0.5" data-testid="type-filters" title="Type de dossier">
            {[['tous', 'AF + AFT'], ['af', 'AF'], ['aft', 'AFT']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setTypeFilter(k)} data-testid={`filter-type-${k}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${typeFilter === k ? (k === 'aft' ? 'bg-cyan-600 text-white' : 'bg-white text-slate-900 shadow-sm') : 'text-slate-500 hover:text-slate-800'}`}>
                {l} <span className="font-normal opacity-70">({typeCounts[k]})</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 sm:ml-auto">
            <button type="button" onClick={() => setShowFilters(v => !v)} data-testid="toggle-col-filters"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${showFilters || nbFiltresActifs ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              <Filter className="w-3.5 h-3.5" /> Filtres colonnes{nbFiltresActifs ? ` (${nbFiltresActifs})` : ''}
            </button>
            {(nbFiltresActifs > 0 || nbQuickActifs > 0 || sort.key) && (
              <button type="button" onClick={() => { setColFilters({ code: '', intitule: '', formation: '', client: '', financeur: '', entreprise: '', lieu: '', date_min: '', date_max: '', candidats_min: '' }); setQuick({ client: 'all', financeur: 'all', entreprise: 'all', lieu: 'all', duree: 'all' }); setSort({ key: null, dir: 'asc' }); }} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="reset-col-filters"><X className="w-3.5 h-3.5" /> Réinitialiser</button>
            )}
          </div>
        </div>

        {/* Filtres rapides : client, lieu, durée */}
        <div className="flex flex-wrap items-center gap-2" data-testid="quick-filters">
          <Select value={quick.client} onValueChange={setQ('client')}>
            <SelectTrigger className="h-9 text-xs w-[220px]" data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les clients</SelectItem>
              {clientsDispo.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={quick.financeur} onValueChange={setQ('financeur')}>
            <SelectTrigger className="h-9 text-xs w-[200px]" data-testid="filter-financeur"><SelectValue placeholder="Financeur" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les financeurs</SelectItem>
              {financeursDispo.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={quick.entreprise} onValueChange={setQ('entreprise')}>
            <SelectTrigger className="h-9 text-xs w-[200px]" data-testid="filter-entreprise"><SelectValue placeholder="Entreprise finale" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les entreprises finales</SelectItem>
              {entreprisesDispo.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={quick.lieu} onValueChange={setQ('lieu')}>
            <SelectTrigger className="h-9 text-xs w-[200px]" data-testid="filter-lieu"><SelectValue placeholder="Lieu" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les lieux</SelectItem>
              {lieuxDispo.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={quick.duree} onValueChange={setQ('duree')}>
            <SelectTrigger className="h-9 text-xs w-[150px]" data-testid="filter-duree"><SelectValue placeholder="Durée" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes durées</SelectItem>
              {dureesDispo.map(h => <SelectItem key={h} value={String(h)}>{h} h{h >= 7 ? ` (${Math.round(h / 7 * 10) / 10} j)` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-400 ml-auto whitespace-nowrap" data-testid="dossiers-count">{filteredDossiers.length}/{dossiers.length} action(s)</span>
        </div>

        {/* Dossiers List */}
        {loading ? (
          <Card><CardContent className="p-6 space-y-3">{[1, 2, 3].map((i) => (<div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />))}</CardContent></Card>
        ) : filteredDossiers.length === 0 && !nbFiltresActifs && !nbQuickActifs ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune action de formation</h3>
              <p className="text-slate-500 mb-4">
                {searchTerm || statutFilter !== 'tous' || nbFiltresActifs || nbQuickActifs ? 'Aucun résultat pour ces critères' : 'Créez votre première action de formation'}
              </p>
              {!searchTerm && statutFilter === 'tous' && !nbFiltresActifs && !nbQuickActifs && (
                <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
                  <Link to="/dossiers/new">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer une action
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="grid grid-cols-[100px_1fr] md:grid-cols-[100px_1fr_1fr_80px_110px_130px_130px_120px_130px_70px] gap-3 bg-slate-100 border-b px-5 py-2.5 text-xs font-medium text-slate-500">
              <SortHeadBase sort={sort} onToggle={toggleSort} k="code" label="Code" />
              <SortHeadBase sort={sort} onToggle={toggleSort} k="intitule" label="Intitulé" />
              <SortHeadBase sort={sort} onToggle={toggleSort} k="formation" label="Formation" className="hidden md:flex" />
              <SortHeadBase sort={sort} onToggle={toggleSort} k="statut" label="Statut" className="hidden md:flex" />
              <SortHeadBase sort={sort} onToggle={toggleSort} k="client" label="Client" className="hidden md:flex" />
              <SortHeadBase sort={sort} onToggle={toggleSort} k="financeur" label="Dossier financeur" className="hidden md:flex" />
              <SortHeadBase sort={sort} onToggle={toggleSort} k="entreprise" label="Entreprise finale" className="hidden md:flex" />
              <SortHeadBase sort={sort} onToggle={toggleSort} k="lieu" label="Lieu" className="hidden md:flex" />
              <SortHeadBase sort={sort} onToggle={toggleSort} k="dates" label="Dates" className="hidden lg:flex" />
              <SortHeadBase sort={sort} onToggle={toggleSort} k="candidats" label="Candidats" className="hidden lg:flex justify-center" />
            </div>
            {showFilters && (
              <div className="grid grid-cols-[100px_1fr] md:grid-cols-[100px_1fr_1fr_80px_110px_130px_130px_120px_130px_70px] gap-3 bg-slate-50 border-b px-5 py-2" data-testid="col-filters-row">
                <FilterInputBase value={colFilters.code} onChange={setCF('code')} k="code" placeholder="Code" />
                <FilterInputBase value={colFilters.intitule} onChange={setCF('intitule')} k="intitule" placeholder="Intitulé contient…" />
                <div className="hidden md:block"><FilterInputBase value={colFilters.formation} onChange={setCF('formation')} k="formation" placeholder="Formation…" /></div>
                <span className="hidden md:block" />
                <div className="hidden md:block"><FilterInputBase value={colFilters.client} onChange={setCF('client')} k="client" placeholder="Client…" /></div>
                <div className="hidden md:block"><FilterInputBase value={colFilters.financeur} onChange={setCF('financeur')} k="financeur" placeholder="Financeur…" /></div>
                <div className="hidden md:block"><FilterInputBase value={colFilters.entreprise} onChange={setCF('entreprise')} k="entreprise" placeholder="Entreprise…" /></div>
                <div className="hidden md:block"><FilterInputBase value={colFilters.lieu} onChange={setCF('lieu')} k="lieu" placeholder="Lieu…" /></div>
                <div className="hidden lg:flex flex-col gap-1"><FilterInputBase value={colFilters.date_min} onChange={setCF('date_min')} k="date_min" type="date" /><FilterInputBase value={colFilters.date_max} onChange={setCF('date_max')} k="date_max" type="date" /></div>
                <div className="hidden lg:block"><FilterInputBase value={colFilters.candidats_min} onChange={setCF('candidats_min')} k="candidats_min" type="number" placeholder="≥" /></div>
              </div>
            )}
            <div className="divide-y">
              {displayRows.length === 0 && <p className="px-5 py-8 text-sm text-slate-400 text-center italic" data-testid="dossiers-empty-filtered">Aucune action ne correspond aux filtres de colonnes</p>}
              {displayRows.map(({ dossier, isChild, color }) => (
                <div
                  key={dossier.id}
                  className={`grid grid-cols-[100px_1fr] md:grid-cols-[100px_1fr_1fr_80px_110px_130px_130px_120px_130px_70px] gap-3 items-center px-5 py-3 hover:bg-slate-50 cursor-pointer transition-colors group ${isChild ? 'bg-slate-50/60' : ''}`}
                  data-testid={`dossier-row-${dossier.id}`}
                  onClick={() => navigate(`/dossiers/${dossier.id}`)}
                >
                  <span className="flex items-center gap-1.5 min-w-0">
                    {isChild && <CornerDownRight className="w-3 h-3 text-slate-400 flex-shrink-0" />}
                    {color && <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${color}`} data-testid={`pastille-groupe-${dossier.id}`} title={isChild ? 'AF rattachée' : 'AF principale du groupe'} />}
                    <span className="text-xs font-mono text-indigo-600 font-medium truncate">{dossier.code}</span>
                    {dossier.type_dossier === 'aft' && (
                      <span className="text-[9px] font-bold text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded shrink-0" title="Action de certification" data-testid={`badge-aft-${dossier.id}`}>AFT</span>
                    )}
                  </span>
                  <p className={`font-medium truncate ${dossier.type_dossier === 'aft' ? 'text-xs text-cyan-900' : 'text-sm text-slate-900'}`}>{dossier.type_dossier === 'aft' ? (dossier.intitule || '').replace(/^Session d'examen\s*/i, '') : dossier.intitule}<span className="md:hidden block text-[11px] text-slate-400 font-normal truncate">{getFinanceurs(dossier) || dossier.client || ''}{dossier.date_debut ? ` · ${format(parseISO(dossier.date_debut), 'dd/MM/yy')}` : ''}</span></p>
                  <div className="hidden md:flex items-center gap-1.5 min-w-0">
                    {dossier.type_dossier === 'aft' ? (
                      <span className="text-xs text-cyan-700 truncate" title="Action de certification rattachée à l'AF mère" data-testid={`aft-mere-ref-${dossier.id}`}>↳ {dossier.af_mere_code || 'AF mère'}</span>
                    ) : <span className="text-sm text-slate-600 truncate">{getFormationTitle(dossier.formation_id)}</span>}
                    {getFormationDuree(dossier.formation_id) > 0 && (
                      <span className="text-[10px] text-slate-400 flex-shrink-0">{getFormationDuree(dossier.formation_id)}h</span>
                    )}
                  </div>
                  <div className="hidden md:flex items-center gap-1 min-w-0">
                    <StatusBadge status={dossier.statut} />
                    {aftsManquantes[dossier.id] && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded shrink-0" title={`Module(s) certifiant(s) sans AFT : ${aftsManquantes[dossier.id].map(m => m.code).join(', ')} — ouvrez l'action pour créer l'AFT`} data-testid={`badge-aft-manquante-${dossier.id}`}>
                        <GraduationCap className="w-2.5 h-2.5" /> AFT manquante{aftsManquantes[dossier.id].length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 truncate hidden md:block">{dossier.client || <span className="text-slate-300">-</span>}</span>
                  <span className="text-xs text-slate-600 truncate hidden md:block" title={dfParAf[dossier.id] ? `${dfParAf[dossier.id].codes.join(', ')} — ${getFinanceurs(dossier)}` : ''} data-testid={`dossier-financeur-${dossier.id}`}>
                    {getFinanceurs(dossier) || <span className="text-slate-300">-</span>}
                  </span>
                  <span className="text-xs text-slate-600 truncate hidden md:block" title={getEntreprises(dossier)} data-testid={`dossier-entreprise-${dossier.id}`}>
                    {getEntreprises(dossier) || <span className="text-slate-300">-</span>}
                  </span>
                  <span className="text-xs text-slate-500 truncate hidden md:flex items-center gap-1" title={dossier.lieu_principal || ''} data-testid={`dossier-lieu-${dossier.id}`}>
                    {dossier.lieu_principal ? <><MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" /><span className="truncate">{dossier.lieu_principal}</span></> : <span className="text-slate-300">-</span>}
                  </span>
                  <span className="text-xs text-slate-400 hidden lg:block">
                    {dossier.date_debut ? format(parseISO(dossier.date_debut), 'dd/MM/yy') : '-'}
                    {dossier.date_fin && ` → ${format(parseISO(dossier.date_fin), 'dd/MM/yy')}`}
                  </span>
                  <span className="text-sm text-slate-500 text-center hidden lg:block">{dossier.candidats?.length || 0}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce dossier ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le dossier et toutes les données associées seront définitivement supprimés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};
